"use client";

import { useMemo } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { MODULAR_INVERSE_CALCULATOR_STRINGS } from "@/components/tools/modular-inverse-calculator";
import { t } from "@/lib/i18n/interpolate";
import { formatBig } from "@/lib/tool-kit/math/bigint";
import {
  type ClassicMethod,
  type Congruence,
  type CrtMerge,
  type CrtResult,
  MAX_CONGRUENCES,
  MAX_DIGITS,
  readSystem,
  solveCrt,
} from "@/lib/tool-kit/number-theory/modular";

type Strings = typeof MODULAR_INVERSE_CALCULATOR_STRINGS;

const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const PROSE_SM = "text-muted-foreground text-sm";

function ClassicTable({
  classic,
  x,
  strings,
}: {
  classic: ClassicMethod;
  x: bigint;
  strings: Strings;
}) {
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.classicHeading}</h3>
      <p className={PROSE_SM}>{strings.classicIntro}</p>
      <DataTable
        head={[
          strings.colRemainderIn,
          strings.colModulus,
          strings.colPartial,
          strings.colInverse,
          strings.colTerm,
        ]}
        rows={classic.rows.map((row) => [
          String(row.congruence.remainder),
          String(row.congruence.modulus),
          String(row.partial),
          String(row.inverse),
          String(row.term),
        ])}
      />
      <p className={PROSE_SM}>
        {t(strings.classicSum, {
          product: String(classic.product),
          sum: String(classic.sum),
          x: String(x),
        })}
      </p>
    </>
  );
}

function MergeStep({ merge, strings }: { merge: CrtMerge; strings: Strings }) {
  const { left, right, gcd, difference, merged, k } = merge;
  const values = {
    a1: String(left.remainder),
    m1: String(left.modulus),
    a2: String(right.remainder),
    m2: String(right.modulus),
    g: String(gcd),
    d: formatBig(difference),
  };
  if (merged === null || k === null) {
    return (
      <div className={MONO_LINES}>
        <p className="text-foreground">{t(strings.mergeHeader, values)}</p>
        <p className="font-sans">{t(strings.noSolution, values)}</p>
      </div>
    );
  }
  return (
    <div className={MONO_LINES}>
      <p className="text-foreground">{t(strings.mergeHeader, values)}</p>
      <p>{t(strings.mergeCheck, values)}</p>
      <p>
        {t(strings.mergeSolve, {
          ...values,
          m1r: String(left.modulus / gcd),
          dr: formatBig(difference / gcd),
          m2r: String(right.modulus / gcd),
          k: String(k),
        })}
      </p>
      <p>
        {t(strings.mergeResult, {
          r: String(merged.remainder),
          m: String(merged.modulus),
        })}
      </p>
    </div>
  );
}

function CrtResults({
  result,
  strings,
}: {
  result: CrtResult;
  strings: Strings;
}) {
  const solution: Congruence | null = result.solution;
  const failed = result.merges.find((merge) => merge.merged === null);
  const summary = solution
    ? t(strings.summaryCrt, {
        r: String(solution.remainder),
        m: String(solution.modulus),
      })
    : "";
  return (
    <>
      {solution ? (
        <>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.smallest}
              value={String(solution.remainder)}
            />
            <StatCard
              label={strings.combinedModulus}
              value={String(solution.modulus)}
            />
          </StatGrid>
          <p className="text-foreground text-sm">{summary}</p>
          <p className={PROSE_SM}>
            {t(strings.general, {
              r: String(solution.remainder),
              m: String(solution.modulus),
            })}
          </p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={summary}
          />
        </>
      ) : null}
      {failed ? (
        <ErrorNotice>
          {t(strings.noSolution, {
            g: String(failed.gcd),
            d: formatBig(failed.difference),
            a1: String(failed.left.remainder),
            m1: String(failed.left.modulus),
            a2: String(failed.right.remainder),
            m2: String(failed.right.modulus),
          })}
        </ErrorNotice>
      ) : null}
      {result.merges.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.mergeHeading}</h3>
          <p className={PROSE_SM}>{strings.mergeIntro}</p>
          {result.merges.map((merge) => (
            <MergeStep
              key={`${merge.left.modulus}-${merge.right.modulus}-${merge.right.remainder}-${merge.left.remainder}`}
              merge={merge}
              strings={strings}
            />
          ))}
        </>
      ) : null}
      {solution && result.classic ? (
        <ClassicTable
          classic={result.classic}
          strings={strings}
          x={solution.remainder}
        />
      ) : null}
      {solution && result.congruences.length > 1 && !result.classic ? (
        <Notice>{strings.notCoprime}</Notice>
      ) : null}
    </>
  );
}

export function CrtOutcome({
  raw,
  strings,
}: {
  raw: string;
  strings: Strings;
}) {
  const read = useMemo(() => readSystem(raw), [raw]);
  const result = useMemo(
    () =>
      read.congruences.length > 0 && !read.tooMany
        ? solveCrt(read.congruences)
        : null,
    [read]
  );
  if (read.invalid.length > 0) {
    return (
      <ErrorNotice>
        {t(strings.badLines, {
          lines: read.invalid.map((l) => `“${l.text}”`).join(", "),
        })}
      </ErrorNotice>
    );
  }
  if (read.smallModulus.length > 0) {
    return (
      <ErrorNotice>
        {t(strings.smallModulus, {
          lines: read.smallModulus.map((l) => l.line).join(", "),
        })}
      </ErrorNotice>
    );
  }
  if (read.tooLong) {
    return <ErrorNotice>{t(strings.tooLong, { max: MAX_DIGITS })}</ErrorNotice>;
  }
  if (read.tooMany) {
    return (
      <ErrorNotice>{t(strings.tooMany, { max: MAX_CONGRUENCES })}</ErrorNotice>
    );
  }
  if (result === null) {
    return <Notice>{strings.enterSystem}</Notice>;
  }
  return <CrtResults result={result} strings={strings} />;
}
